import type { IContentfulError } from "@services/sdk";
import {
	type ErrorResponseMap,
	ErrorResponseMapDefs,
} from "@services/sdk/models/error-response-map";
import type { AxiosError } from "axios";

class ErrorResponse {
	private static DefaultErrorResponseMap: ErrorResponseMap =
		ErrorResponseMapDefs.Defaults;

	public readonly message: string; // where the client side message goes

	public detail: string | Array<string> | Array<object> = ""; // where the message from the server goes if there is one

	public readonly statusCode: number = 0;

	public static ConstructMRTMErrorResponse(
		errorMap: ErrorResponseMap,
		error: AxiosError,
	): ErrorResponse {
		const errorResponse = ErrorResponse.ConstructFromAxiosError(
			errorMap,
			error,
		);

		if (
			!error.response ||
			!error.response.data ||
			!(error.response.data as any).message
		) {
			return errorResponse;
		}

		const data = error.response.data as any;
		errorResponse.detail = data.message;

		Object.assign(errorResponse, data);

		return errorResponse;
	}

	public static ConstructPSMAErrorResponse(
		errorMap: ErrorResponseMap,
		error: AxiosError,
	): ErrorResponse {
		function getDetail() {
			if (!error.response || !error.response.data) {
				return "";
			}

			const { data }: { data: any } = error.response;

			if (data.messages) {
				return data.messages;
			}

			if (data.error) {
				return data.error.description;
			}

			if (data.fault) {
				return data.fault.faultstring;
			}

			return JSON.stringify(error.response.data);
		}

		const errorResponse = ErrorResponse.ConstructFromAxiosError(
			errorMap,
			error,
		);

		errorResponse.detail = getDetail();

		return errorResponse;
	}

	public static ConstructContentfulErrorResponse(
		contentfulError: IContentfulError,
	): ErrorResponse {
		const message = contentfulError.message
			? contentfulError.message
			: JSON.stringify(contentfulError);
		const statusCode = 0;
		const detail = contentfulError.details?.errors
			? contentfulError.details.errors
			: JSON.stringify(contentfulError);

		return new ErrorResponse(message, statusCode, detail);
	}

	public static ConstructFromAxiosError(
		errorMap: ErrorResponseMap,
		error: AxiosError,
	) {
		let message = "";
		let statusCode = 0;
		const detail = "";

		if (!error.response) {
			message = ErrorResponse.DefaultErrorResponseMap["500"];
			return new ErrorResponse(message, statusCode, detail);
		}

		statusCode = error.response.status;

		if (!statusCode) {
			message = ErrorResponse.DefaultErrorResponseMap["500"];
			return new ErrorResponse(message, statusCode, detail);
		}

		const errorMessage = errorMap[statusCode];
		if (errorMessage) {
			message = errorMessage;
			return new ErrorResponse(message, statusCode, detail);
		}

		const defaultErrorMessage =
			ErrorResponse.DefaultErrorResponseMap[statusCode];
		if (defaultErrorMessage) {
			message = defaultErrorMessage;
			return new ErrorResponse(message, statusCode, detail);
		}

		message = ErrorResponse.DefaultErrorResponseMap["500"];
		return new ErrorResponse(message, statusCode, detail);
	}

	public static NetworkError(): ErrorResponse {
		return new ErrorResponse("Network Error", 503, "");
	}

	constructor(
		message: string,
		statusCode: number,
		detail: string | Array<string> | Array<object>,
	) {
		this.message = message;
		this.statusCode = statusCode;
		this.detail = detail;
	}
}

export default ErrorResponse;
